import { formatNumber, formatPercent } from "@angular/common";
import { Directive, ElementRef, forwardRef, HostListener, Inject, Input, LOCALE_ID } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { LgdNumberFormatType } from "../lgdCommon";

export interface LgdNumberConfig
{
    precision?: number;
    scale?: number;
    filter?: LgdNumberFormatType;
    noCommas?: boolean;
}

@Directive({
    selector: "input[lgdNumber]",
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LgdNumberValueAccessor),
            multi: true,
        }],
    standalone: false
})
export class LgdNumberValueAccessor implements ControlValueAccessor
{
    private hasFocus: boolean;

    private _value: string | null;

    private _onChange: (value: number) => void;

    private _onTouched: () => void;

    @Input()
    readonly lgdNumber: Readonly<LgdNumberConfig>;

    @Input("value")
    get value(): string | null
    {
        return this._value;
    }

    set value(value: string | null)
    {
        this.writeValue(this._value);
    }

    @HostListener("input", ["$event"])
    onInput(_event: KeyboardEvent): void
    {
        const value = this.elementRef.nativeElement.value;
        this.fireOnChange(value);
    }

    @HostListener("blur")
    _onBlur(): void
    {
        this.format(this.elementRef.nativeElement.value);
        this.fireOnTouched();
        this.hasFocus = false;
    }

    @HostListener("focus")
    onFocus(): void
    {
        if (this.hasFocus)
        {
            return;
        }
        // remove commas for editing purpose
        this.unFormat(this.elementRef.nativeElement.value);
        this.hasFocus = true;
        this.elementRef.nativeElement.select();
    }

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string,
        private readonly elementRef: ElementRef<HTMLInputElement>,
    ) { }

    /** @override ControlValueAccessor.writeValue */
    writeValue(value: string): void
    {
        this._value = value;
        this.format(value);
    }

    /** @override ControlValueAccessor.registerOnChange */
    registerOnChange(fn: (value: unknown) => void): void
    {
        this._onChange = fn;
    }

    /** @override ControlValueAccessor.registerOnTouched */
    registerOnTouched(fn: () => void): void
    {
        this._onTouched = fn;
    }

    private fireOnChange(value: string): void
    {
        if (!this._onChange)
        {
            return;
        }
        this._onChange(parseFloat(value));
    }

    private fireOnTouched(): void
    {
        if (!this._onTouched) return;
        this._onTouched();
    }

    private format(rawValue: number | string): void
    {
        const value = this.toNumber(rawValue);
        if (value === undefined)
        {
            this.elementRef.nativeElement.value = "";
            return;
        }
        const scale = this.lgdNumber.scale || 0;
        /*
         * The digitsInfo for both transform functions currently looks compatible, so we should be able to use the same
         * logic between the two.
         *
         * The default is equivalent to '1.2-2' for formatNumber; as such this pipe will truncate the value to only two decimal places.
         * @see https://v17.angular.io/api/common/formatNumber
         *
         * The default is equivalent to '1.0-0' for formatPercent.
         * @see https://v17.angular.io/api/common/formatPercent
         */
        const digitsInfo = scale > 0 ? `1.${scale}-${scale}` : undefined;
        let elementValue =
            this.lgdNumber.filter === "percent"
                ? this.formatPercent(value, digitsInfo)
                : this.formatDecimal(value, digitsInfo);
        if (this.lgdNumber.noCommas)
        {
            elementValue = this.transformNoCommaDecimal(elementValue);
        }
        this.elementRef.nativeElement.value = elementValue;
    }

    private transformNoCommaDecimal(value: string): string
    {
        if (value !== undefined && value !== null)
        {
            return value.toString().replace(/,/g, "");
        }
        else
        {
            return "";
        }
    }

    private toNumber(value: number | string): number | undefined
    {
        // ! Explicity testing for `undefined` and `null` instead of relying on truthy eval because 0 is a valid value in this case.
        if (value === undefined && value === null)
        {
            return undefined;
        }
        if (typeof value !== "number")
        {
            value = parseFloat(value);
        }
        return !isNaN(value) ? value : undefined;
    }

    private formatDecimal(value: number, digitsInfo: string | undefined): string
    {
        return formatNumber(value, this.locale, digitsInfo);
    }

    private formatPercent(value: number, digitsInfo: string | undefined): string
    {
        return formatPercent(value / 100, this.locale, digitsInfo);
    }

    private unFormat(value: string): void
    {
        const rawValue = value ? value.replace(/[^\d.-]/g, "") : "";
        this.elementRef.nativeElement.value = rawValue;
    }

    setDisabledState(isDisabled: boolean): void
    {
        this.elementRef.nativeElement.disabled = isDisabled;
    }
}
